'use client'

import { ArrowRight, MapPin } from 'lucide-react'
import dynamic from 'next/dynamic'
import Link from 'next/link'
import React, { useEffect, useId, useMemo, useRef, useState } from 'react'

import { containerClassName } from '@/components/Container'
import { LocationHeroUniversityTravelStats } from '@/components/Location/LocationHeroUniversityTravelStats'
import { LocationMapUniversityFinderControls } from '@/components/Location/LocationMapUniversityFinderControls'
import { Button } from '@/components/ui/button'
import { getCityBySlug, type CityAreaMapCameraPadding } from '@/lib/mock/cities'
import {
  getCities,
  getDefaultMapViewId,
  getMapView,
  type CityId,
  type MapViewId,
} from '@/lib/mock/mapLocations'
import type { MapCityCardData } from '@/lib/cities/mapCityData'
import type { MapLocationCardData } from '@/lib/locations/mapLocationData'
import {
  getLandingMapSelectedLocationId,
  useCityMapUniversityFinder,
} from '@/lib/locations/useCityMapUniversityFinder'
import { localizePath } from '@/lib/i18n/config'
import { useLocale } from '@/lib/i18n/useLocale'
import { useMessages } from '@/lib/i18n/useMessages'
import { cn } from '@/utilities/ui'

const COMPACT_DESKTOP_MAP_BREAKPOINT_PX = 1400

const LocationAreaMap = dynamic(
  () => import('@/components/Location/LocationAreaMap').then((mod) => mod.LocationAreaMap),
  { ssr: false },
)

type LocationsMapSectionProps = {
  mapCities: MapCityCardData[]
  mapLocations: MapLocationCardData[]
}

type LocationsMapCopy = {
  cityListAriaLabel: string
  cityTabAriaLabel: string
  cities: Record<CityId, string>
  emptyState: string
  eyebrow: string
  locationListAriaLabel: string
  locationPinAriaLabel: string
  locationTabAriaLabel: string
  mapAlt: string
  mapAltOverview: string
  mapUnavailable: string
  rentNow: string
  rentNowHref: string
  statApartmentsLabel: string
  statApartmentsValue: string
  statLocationsLabel: string
  statLocationsValue: string
  statStudentsLabel: string
  statStudentsValue: string
  titleLine1: string
  titleLine2: string
  viewCityLabel: string
}

type MapTabProps = {
  ariaLabel: string
  badgeClassName: string
  count: number
  id: CityId
  isSelected: boolean
  label: string
  onSelect: (cityId: CityId) => void
  tabClassName: string
  activeTabClassName: string
}

const MOBILE_MAP_MEDIA_QUERY = '(max-width: 767px)'
const NARROW_MOBILE_MAP_MEDIA_QUERY = '(max-width: 575px)'
const VERY_NARROW_MOBILE_MAP_MEDIA_QUERY = '(max-width: 399px)'
const COMPACT_DESKTOP_MAP_MEDIA_QUERY = `(max-width: ${COMPACT_DESKTOP_MAP_BREAKPOINT_PX - 1}px) and (min-width: 768px)`
const MOBILE_MAP_ZOOM_OFFSET = -1.5
const NARROW_MOBILE_MAP_ZOOM_OFFSET = -1.75
const VERY_NARROW_BERLIN_MAP_ZOOM_OFFSET = -2
const BERLIN_MOBILE_ZOOM_BOOST = 1
const MOBILE_OVERVIEW_MAP_ZOOM_OFFSET = -1
const NARROW_MOBILE_OVERVIEW_MAP_ZOOM_OFFSET = -1.25
const VERY_NARROW_MOBILE_OVERVIEW_MAP_ZOOM_OFFSET = -1.4
const MOBILE_MAP_HEIGHT_CLASS = 'max-md:h-[70svh]'

function getMobileMapCameraPadding(isOverview: boolean): CityAreaMapCameraPadding {
  return {
    bottom: 20,
    centerLatOffset: isOverview ? 0.08 : 0,
    disableContentInset: true,
    minLeft: 20,
    ...(isOverview ? { overviewFitAtZoom: true } : {}),
    right: 20,
    top: 36,
  }
}

function MapTab({
  activeTabClassName,
  ariaLabel,
  badgeClassName,
  count,
  id,
  isSelected,
  label,
  onSelect,
  tabClassName,
}: MapTabProps) {
  return (
    <li>
      <button
        aria-label={ariaLabel}
        aria-pressed={isSelected}
        className={cn(
          'inline-flex h-9 items-center gap-1 rounded-full border border-black/10 px-3 py-1.5 font-outfit text-base font-medium text-dark-800/90 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-turquoise/50 focus-visible:ring-offset-2',
          isSelected ? activeTabClassName : tabClassName,
        )}
        onClick={() => onSelect(id)}
        type="button"
      >
        <MapPin aria-hidden className="size-4 shrink-0" />
        <span>{label}</span>
        <span
          className={cn(
            'inline-flex size-5 items-center justify-center rounded-full font-display text-sm font-medium text-dark-800/80',
            isSelected ? 'bg-turquoise' : badgeClassName,
          )}
        >
          {count}
        </span>
      </button>
    </li>
  )
}

export function LocationsMapSection({ mapCities, mapLocations }: LocationsMapSectionProps) {
  const messages = useMessages()
  const locale = useLocale()
  const copy = messages.landing.locationsMap as LocationsMapCopy
  const mapControlLabels = messages.mapControls
  const cityListId = useId()
  const locationListId = useId()
  const cities = useMemo(() => getCities(), [])
  const [selectedMapViewId, setSelectedMapViewId] = useState<MapViewId>(getDefaultMapViewId())
  const [isMobileMap, setIsMobileMap] = useState(
    () => typeof window !== 'undefined' && window.matchMedia(MOBILE_MAP_MEDIA_QUERY).matches,
  )
  const [isNarrowMobileMap, setIsNarrowMobileMap] = useState(
    () =>
      typeof window !== 'undefined' &&
      window.matchMedia(NARROW_MOBILE_MAP_MEDIA_QUERY).matches,
  )
  const [isVeryNarrowMobileMap, setIsVeryNarrowMobileMap] = useState(
    () =>
      typeof window !== 'undefined' &&
      window.matchMedia(VERY_NARROW_MOBILE_MAP_MEDIA_QUERY).matches,
  )
  const [isCompactDesktopMap, setIsCompactDesktopMap] = useState(
    () =>
      typeof window !== 'undefined' && window.matchMedia(COMPACT_DESKTOP_MAP_MEDIA_QUERY).matches,
  )

  const mapView = useMemo(() => {
    const view = getMapView(selectedMapViewId)

    if (!view.isOverview) {
      return view
    }

    return {
      ...view,
      locations: view.locations.map((location) => ({
        ...location,
        name: copy.cities[location.city],
      })),
    }
  }, [copy.cities, selectedMapViewId])

  const displayMapView = useMemo(() => {
    if (!isMobileMap) return mapView

    const berlinMobileBoost = mapView.id === 'berlin' ? BERLIN_MOBILE_ZOOM_BOOST : 0
    const mobileZoomOffset =
      (mapView.isOverview
        ? isVeryNarrowMobileMap
          ? VERY_NARROW_MOBILE_OVERVIEW_MAP_ZOOM_OFFSET
          : isNarrowMobileMap
            ? NARROW_MOBILE_OVERVIEW_MAP_ZOOM_OFFSET
            : MOBILE_OVERVIEW_MAP_ZOOM_OFFSET
        : isVeryNarrowMobileMap && mapView.id === 'berlin'
          ? VERY_NARROW_BERLIN_MAP_ZOOM_OFFSET
          : isNarrowMobileMap
            ? NARROW_MOBILE_MAP_ZOOM_OFFSET
            : MOBILE_MAP_ZOOM_OFFSET) + berlinMobileBoost

    return {
      ...mapView,
      zoom: mapView.zoom + mobileZoomOffset,
      locations: mapView.locations.map((location) =>
        location.zoom == null
          ? location
          : { ...location, zoom: location.zoom + mobileZoomOffset },
      ),
    }
  }, [isMobileMap, isNarrowMobileMap, isVeryNarrowMobileMap, mapView])

  const selectedCity =
    selectedMapViewId === 'overview'
      ? undefined
      : cities.find((city) => city.id === selectedMapViewId)
  const selectedCitySlug =
    selectedMapViewId === 'overview' ? undefined : selectedMapViewId
  const marketingCity = selectedCitySlug ? getCityBySlug(selectedCitySlug) : undefined
  const mapContainerRef = useRef<HTMLDivElement>(null)
  const [showMap, setShowMap] = useState(false)
  const mapHeroCopy = messages.location.mapHero
  const finder = useCityMapUniversityFinder({
    city: marketingCity,
    citySlug: selectedCitySlug,
    isCityMode: true,
    mapContainerRef,
    mapHeroCopy: {
      approximateDistanceLabel: mapHeroCopy.approximateDistanceLabel,
      distanceFromToLabel: mapHeroCopy.distanceFromToLabel,
      distanceToLabel: mapHeroCopy.distanceToLabel,
      proximityLabel: mapHeroCopy.proximityLabel,
      routeUnavailable: mapHeroCopy.routeUnavailable,
    },
  })
  const universityRoute = useMemo(() => {
    if (!finder.showUniversityRoute || !finder.universityCoordinate || !finder.routeMarker) {
      return undefined
    }

    return {
      campusCoordinate: [finder.routeMarker.longitude, finder.routeMarker.latitude] as [
        number,
        number,
      ],
      routeAnimationId: finder.routeAnimationId,
      routeDirection: finder.routeDirection,
      universityCoordinate: finder.universityCoordinate,
      universityName: finder.selectedUniversity?.name ?? '',
    }
  }, [
    finder.routeAnimationId,
    finder.routeDirection,
    finder.routeMarker,
    finder.selectedUniversity?.name,
    finder.showUniversityRoute,
    finder.universityCoordinate,
  ])

  const resolvedMapCameraPadding = useMemo(() => {
    if (isMobileMap) {
      return getMobileMapCameraPadding(selectedMapViewId === 'overview')
    }

    if (isCompactDesktopMap && selectedMapViewId === 'overview') {
      return {
        centerLatOffset: 0.2,
        centerLngOffset: 0.45,
        extraLeft: -40,
        overviewFitAtZoom: true,
        right: 128,
      }
    }

    return undefined
  }, [isCompactDesktopMap, isMobileMap, selectedMapViewId])

  useEffect(() => {
    const mobileMedia = window.matchMedia(MOBILE_MAP_MEDIA_QUERY)
    const narrowMobileMedia = window.matchMedia(NARROW_MOBILE_MAP_MEDIA_QUERY)
    const veryNarrowMobileMedia = window.matchMedia(VERY_NARROW_MOBILE_MAP_MEDIA_QUERY)
    const compactDesktopMedia = window.matchMedia(COMPACT_DESKTOP_MAP_MEDIA_QUERY)

    const updateLayoutMode = () => {
      setIsMobileMap(mobileMedia.matches)
      setIsNarrowMobileMap(narrowMobileMedia.matches)
      setIsVeryNarrowMobileMap(veryNarrowMobileMedia.matches)
      setIsCompactDesktopMap(compactDesktopMedia.matches)
    }

    updateLayoutMode()
    mobileMedia.addEventListener('change', updateLayoutMode)
    narrowMobileMedia.addEventListener('change', updateLayoutMode)
    veryNarrowMobileMedia.addEventListener('change', updateLayoutMode)
    compactDesktopMedia.addEventListener('change', updateLayoutMode)
    window.addEventListener('resize', updateLayoutMode)

    return () => {
      mobileMedia.removeEventListener('change', updateLayoutMode)
      narrowMobileMedia.removeEventListener('change', updateLayoutMode)
      veryNarrowMobileMedia.removeEventListener('change', updateLayoutMode)
      compactDesktopMedia.removeEventListener('change', updateLayoutMode)
      window.removeEventListener('resize', updateLayoutMode)
    }
  }, [])

  useEffect(() => {
    const node = mapContainerRef.current
    if (!node || showMap) return

    if (typeof IntersectionObserver === 'undefined') {
      setShowMap(true)
      return
    }

    const observer = new IntersectionObserver(
      (entries) => {
        if (!entries.some((entry) => entry.isIntersecting)) return
        setShowMap(true)
        observer.disconnect()
      },
      { rootMargin: '120px' },
    )

    observer.observe(node)
    return () => observer.disconnect()
  }, [showMap])

  if (cities.length === 0) {
    return (
      <section aria-labelledby="locations-map-heading" className="bg-cream py-16 md:py-24">
        <div className={containerClassName}>
          <h2
            className="font-display text-[2rem] font-semibold text-bluetext md:text-[4rem] md:leading-tight"
            id="locations-map-heading"
          >
            {copy.titleLine1}
            <br />
            {copy.titleLine2}
          </h2>
          <p className="mt-4 font-outfit text-base text-dark-800">{copy.emptyState}</p>
        </div>
      </section>
    )
  }

  const handleCitySelect = (cityId: CityId) => {
    if (selectedMapViewId === cityId) {
      setSelectedMapViewId('overview')
      return
    }

    const city = cities.find((entry) => entry.id === cityId)
    if (!city) return

    setSelectedMapViewId(cityId)
  }

  const selectedLocationId = getLandingMapSelectedLocationId(
    selectedCitySlug,
    finder.selectedCampusSlug,
  )

  return (
    <section
      aria-labelledby="locations-map-heading"
      className="relative overflow-hidden bg-cream max-md:flex max-md:flex-col md:min-h-[68rem]"
      data-locations-map-section
    >
      <div
        ref={mapContainerRef}
        className={cn(
          'locations-map-section absolute inset-0 z-0',
          'max-md:relative max-md:inset-auto max-md:order-2 max-md:w-full max-md:shrink-0',
          MOBILE_MAP_HEIGHT_CLASS,
        )}
      >
        {showMap ? (
          <LocationAreaMap
            className="size-full rounded-none md:min-h-0"
            fallbackMessage={copy.mapUnavailable}
            mapCameraPadding={resolvedMapCameraPadding}
            mapCities={mapCities}
            mapControlLabels={mapControlLabels}
            mapLocations={mapLocations}
            mapView={displayMapView}
            selectedLocationId={selectedLocationId}
            universityRoute={universityRoute}
            viewCityLabel={copy.viewCityLabel}
          />
        ) : null}

        <div
          aria-hidden
          className={cn(
            'pointer-events-none absolute inset-0 z-[2]',
            // Top + bottom cream fades so the map soft-blends into the section
            'bg-[linear-gradient(to_bottom,#FBFAF1_0%,rgba(251,250,241,0)_12%),linear-gradient(to_top,#FBFAF1_0%,rgba(251,250,241,0)_12%)]',
            // Keep a stronger left-side wash for copy readability on desktop
            'md:bg-[linear-gradient(to_bottom,#FBFAF1_0%,rgba(251,250,241,0)_12%),linear-gradient(to_top,#FBFAF1_0%,rgba(251,250,241,0)_12%),radial-gradient(circle_at_0%_0%,#FBFAF1_0%,rgba(251,250,241,0.92)_40%,rgba(251,250,241,0)_50%)]',
          )}
        />
      </div>

      <div className={cn(containerClassName, 'pointer-events-none relative z-30 max-md:order-1 max-md:shrink-0')}>
        <div
          className="pointer-events-none max-w-[40rem] md:max-[1399px]:pt-16 lg:max-[1399px]:pt-24 min-[1400px]:md:pt-48 min-[1400px]:lg:pt-56"
          data-locations-map-content
        >
          <p className="pointer-events-auto font-outfit text-base font-medium text-bluetext">
            {copy.eyebrow}
          </p>
          <h2
            className="pointer-events-auto mt-4 font-display text-[2.5rem] font-medium leading-10 tracking-[-0.04em] text-bluetext md:mt-3 md:text-[4rem] md:font-semibold md:leading-[1]"
            id="locations-map-heading"
          >
            <span className="md:hidden">
              {copy.titleLine1} {copy.titleLine2}
            </span>
            <span className="hidden md:block">
              {copy.titleLine1}
              <br />
              {copy.titleLine2}
            </span>
          </h2>

          <ul
            aria-label={copy.cityListAriaLabel}
            className="pointer-events-auto mt-4 flex w-full  flex-wrap gap-2 md:mt-10 md:max-w-none md:gap-2.5"
            id={cityListId}
          >
            {cities.map((city) => (
              <MapTab
                activeTabClassName={city.activeTabClassName}
                ariaLabel={copy.cityTabAriaLabel.replace('{city}', copy.cities[city.id])}
                badgeClassName={city.badgeClassName}
                count={city.count}
                id={city.id}
                isSelected={city.id === selectedMapViewId}
                key={city.id}
                label={copy.cities[city.id]}
                onSelect={handleCitySelect}
                tabClassName={city.tabClassName}
              />
            ))}
          </ul>

          {selectedCity && selectedCity.locations.length > 1 ? (
            <ul aria-label={copy.locationListAriaLabel} className="sr-only" id={locationListId}>
              {selectedCity.locations.map((location) => (
                <li key={location.id}>
                  <button
                    aria-label={copy.locationTabAriaLabel.replace('{location}', location.name)}
                    aria-pressed={location.id === selectedLocationId}
                    onClick={() => {
                      const campus = finder.campuses.find((entry) => entry.slug === location.id)
                      if (campus) {
                        finder.handleCampusSelect(campus)
                      }
                    }}
                    type="button"
                  >
                    {location.name}
                  </button>
                </li>
              ))}
            </ul>
          ) : null}

          {selectedCitySlug && marketingCity ? (
            <>
              {finder.isUniversityFinder ? (
                <p className="pointer-events-auto mt-4 max-w-[36rem] font-outfit text-base font-medium text-dark-800/80 md:mt-6">
                  {mapHeroCopy.helperText}
                </p>
              ) : null}

              <LocationMapUniversityFinderControls
                campuses={finder.campuses}
                citySlug={selectedCitySlug}
                exploreLabel={marketingCity.areaMap.ctaLabel}
                isUniversityFinder={finder.isUniversityFinder}
                mapHeroCopy={mapHeroCopy}
                onCampusClear={finder.handleCampusClear}
                onCampusSelect={finder.handleCampusSelect}
                onUniversityClear={finder.handleUniversityClear}
                onUniversitySelect={finder.handleUniversitySelect}
                routeUnavailableMessage={finder.routeUnavailableMessage}
                selectedCampusSlug={finder.selectedCampusSlug}
                selectedUniversity={finder.selectedUniversity}
                universities={finder.universities}
                universityCampusOptions={finder.universityCampusOptions}
              />

              {finder.universityTravelStats?.length && finder.selectedUniversity ? (
                <div
                  className={cn(
                    'pointer-events-auto',
                    finder.isUniversityFinder ? 'mt-4 md:mt-6' : 'mt-6 md:mt-10',
                  )}
                >
                  <LocationHeroUniversityTravelStats
                    compact={finder.isUniversityFinder}
                    destinationLabel={finder.travelStatsDestinationLabel}
                    stats={finder.universityTravelStats}
                    theme="light"
                    universityName={finder.selectedUniversity.name}
                  />
                </div>
              ) : null}
            </>
          ) : (
            <dl className="pointer-events-auto mt-7 grid max-w-md grid-cols-3 gap-x-2 md:mt-10 md:gap-4 md:pt-6">
              <div className="flex flex-col items-center text-center md:items-start md:text-left">
                <dt className="sr-only">{copy.statLocationsLabel}</dt>
                <dd className="font-display text-[2rem] font-medium leading-none tracking-[-0.04em] text-dark-800/80">
                  {copy.statLocationsValue}
                </dd>
                <dd className="mt-2 max-w-[4.5rem] font-outfit text-base leading-4 text-dark-800/80">
                  {copy.statLocationsLabel}
                </dd>
              </div>
              <div className="flex flex-col items-center border-l border-black/10 pl-2 text-center md:items-start md:border-l md:pl-4 md:text-left">
                <dt className="sr-only">{copy.statApartmentsLabel}</dt>
                <dd className="font-display text-[2rem] font-medium leading-none tracking-[-0.04em] text-dark-800/80">
                  {copy.statApartmentsValue}
                </dd>
                <dd className="mt-2 max-w-[5.5rem] font-outfit text-base leading-4 text-dark-800/80">
                  {copy.statApartmentsLabel}
                </dd>
              </div>
              <div className="flex flex-col items-center border-l border-black/10 pl-2 text-center md:items-start md:border-l md:pl-4 md:text-left">
                <dt className="sr-only">{copy.statStudentsLabel}</dt>
                <dd className="font-display text-[2rem] font-medium leading-none tracking-[-0.04em] text-dark-800/80">
                  {copy.statStudentsValue}
                </dd>
                <dd className="mt-2 max-w-[6.5rem] font-outfit text-base leading-4 text-dark-800/80">
                  {copy.statStudentsLabel}
                </dd>
              </div>
            </dl>
          )}

          <div className="pointer-events-auto mt-6 md:mt-10 pb-4 md:pb-16">
            <Button
              asChild
              className="h-12 gap-2 border border-black/10 px-4 font-outfit text-base font-medium"
            >
              <Link href={localizePath(copy.rentNowHref, locale)}>
                {copy.rentNow}
                <ArrowRight aria-hidden className="size-4" />
              </Link>
            </Button>
          </div>
        </div>

        <p className="sr-only">
          {selectedMapViewId === 'overview'
            ? copy.mapAltOverview
            : copy.mapAlt.replace('{city}', copy.cities[selectedMapViewId as CityId])}
        </p>
      </div>
    </section>
  )
}
